'use client';

import { useMemo } from 'react';
import { pageBackgroundCss } from '@/lib/page-background';

/**
 * A seller's background colour, in the page's own HTML.
 *
 * ThemeProvider paints the colour from the design override, but the page
 * publishes that override in an effect, after hydration, so on its own the
 * server HTML is the stock page in the visitor's theme and the whole page
 * swaps colour (and light or dark) once the JavaScript has run. The product
 * page and the storefront render on the server from the seller's design
 * already, so each also renders this: one stylesheet rule with the same
 * palette (pageBackgroundCss), present from the first paint and gone with the
 * page. It follows the live design like the rest of the page, and renders
 * the same string on the server and in the browser's first render, so it
 * cannot mismatch on hydration.
 *
 * `color` is the resolved override's backgroundColor, already validated, or
 * null for a page without one, which renders nothing.
 */
export function SellerPageBackground({ color }: { color: string | null | undefined }) {
  const css = useMemo(() => pageBackgroundCss(color), [color]);
  if (!css) return null;
  return <style data-seller-page-background="">{css}</style>;
}
